import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ThemeToggle } from '../components/ThemeToggle';
import { 
  ArrowRight, 
  QrCode, 
  Receipt, 
  CreditCard, 
  TrendingUp,
  Shield,
  Zap,
  Building2,
  User,
  CheckCircle
} from 'lucide-react';

const Home = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirecionar se já estiver autenticado
  React.useEffect(() => {
    if (!loading && user) {
      if (user.role === 'BANKER') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        // Customer - verificar se precisa de onboarding será feito na rota
        navigate('/app/dashboard', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  // Mostrar loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const features = [
    {
      icon: QrCode,
      title: 'Pix Instantâneo',
      description: 'Envie e receba dinheiro em segundos, 24 horas por dia, 7 dias por semana.',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      icon: Receipt,
      title: 'Pagamento de Boletos',
      description: 'Pague seus boletos de forma rápida e segura, direto pelo app.',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      icon: CreditCard,
      title: 'Limites Personalizados',
      description: 'Limites operacionais e cheque especial adaptados ao seu perfil.',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      icon: TrendingUp,
      title: 'Empréstimos e Crédito',
      description: 'Acesso a crédito com taxas competitivas e aprovação simplificada.',
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    }
  ];

  const benefits = [
    { icon: Shield, text: 'Segurança bancária' },
    { icon: Zap, text: 'Abertura em minutos' },
    { icon: CheckCircle, text: 'Sem taxas ocultas' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
              <span className="text-lg font-bold text-primary-foreground">F</span>
            </div>
            <span className="text-xl font-bold tracking-tight">FinApp</span>
          </Link>
          
          <nav className="flex items-center gap-3">
            <ThemeToggle />
            <Button 
              variant="ghost" 
              className="rounded-full"
              onClick={() => navigate('/login')}
              data-testid="header-login-btn"
            >
              Entrar
            </Button>
            <Button 
              className="rounded-full"
              onClick={() => navigate('/open-account')}
              data-testid="header-open-account-btn"
            >
              Abrir conta
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
              <Zap className="h-4 w-4" />
              Conta 100% digital
            </div>
            
            <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Sua conta digital completa para{' '}
              <span className="text-primary">PF e PJ</span>
            </h1>
            
            <p className="mb-8 text-lg text-muted-foreground md:text-xl">
              Pix instantâneo, pagamento de boletos, limites flexíveis e crédito sob medida. 
              Tudo em um único lugar, com a segurança que você precisa.
            </p>

            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button 
                size="lg" 
                className="h-12 rounded-full px-8 text-base"
                onClick={() => navigate('/open-account')}
                data-testid="hero-open-account-btn"
              >
                Abrir conta agora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="h-12 rounded-full px-8 text-base"
                onClick={() => navigate('/login')}
              >
                Já sou cliente
              </Button>
            </div>

            {/* Trust badges */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <benefit.icon className="h-4 w-4 text-primary" />
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="border-t border-border/40 bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold tracking-tight">
              Tudo o que você precisa em uma conta
            </h2>
            <p className="text-muted-foreground">
              Funcionalidades pensadas para simplificar sua vida financeira
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group border-border/50 transition-all hover:shadow-lg hover:border-primary/30"
              >
                <CardContent className="pt-6">
                  <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl ${feature.bgColor}`}>
                    <feature.icon className={`h-6 w-6 ${feature.color}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="mb-2 font-semibold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - PF ou PJ */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 p-8 md:p-12">
              <div className="mb-8 text-center">
                <h2 className="mb-4 text-3xl font-bold tracking-tight">
                  Abra sua conta em minutos
                </h2>
                <p className="text-muted-foreground">
                  Escolha o tipo de conta ideal para você ou sua empresa
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* PF Card */}
                <Card 
                  className="group cursor-pointer border-2 transition-all hover:border-primary hover:shadow-xl"
                  onClick={() => navigate('/open-account?type=PF')}
                  data-testid="cta-pf-card"
                >
                  <CardContent className="flex flex-col items-center p-8 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/20">
                      <User className="h-8 w-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="mb-2 text-xl font-bold">Pessoa Física</h3>
                    <p className="mb-4 text-sm text-muted-foreground">
                      Conta individual para gerenciar suas finanças pessoais com CPF
                    </p>
                    <Button className="rounded-full group-hover:bg-primary">
                      Abrir conta PF
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>

                {/* PJ Card */}
                <Card 
                  className="group cursor-pointer border-2 transition-all hover:border-primary hover:shadow-xl"
                  onClick={() => navigate('/open-account?type=PJ')}
                  data-testid="cta-pj-card"
                >
                  <CardContent className="flex flex-col items-center p-8 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10 transition-colors group-hover:bg-accent/20">
                      <Building2 className="h-8 w-8 text-accent-foreground" strokeWidth={1.5} />
                    </div>
                    <h3 className="mb-2 text-xl font-bold">Pessoa Jurídica</h3>
                    <p className="mb-4 text-sm text-muted-foreground">
                      Conta empresarial completa para seu negócio com CNPJ
                    </p>
                    <Button variant="outline" className="rounded-full group-hover:bg-accent group-hover:text-accent-foreground">
                      Abrir conta PJ
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-muted/30">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center gap-4 text-center md:flex-row md:justify-between md:text-left">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-sm font-bold text-primary-foreground">F</span>
              </div>
              <span className="font-semibold">FinApp</span>
              <span className="text-sm text-muted-foreground">• Conta Digital</span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              © 2025 FinApp. Todos os direitos reservados.
            </p>
          </div>
          
          <div className="mt-6 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-4 text-center">
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              <strong>Ambiente de Demonstração (MVP):</strong> As integrações com Pix e Boleto são simuladas (mock). 
              Nenhuma transação financeira real é processada.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
